export default {
  name: "REQUEST_MODULE",
  namespaced: true,
  state: {
    requests: [],
    currentRequest: null,
    loading: false,
    error: null,
  },
  getters: {
    getAllRequests: (state) => state.requests,
    getAllUserRequests: (state) => state.requests,
    currentRequest: (state) => state.currentRequest,
    isLoading: (state) => state.loading,
    hasError: (state) => state.error !== null,
    errorMessage: (state) => state.error,
    getApiKey: (state) => state.api_key,
  },
  mutations: {
    SET_REQUESTS(state, requests) {
      state.requests = requests;
    },
    SET_CURRENT_REQUEST(state, request) {
      state.currentRequest = request;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    CLEAR_ERROR(state) {
      state.error = null;
    },
  },
  actions: {
    // Fetch all requests with optional date range filtering
    async fetchAllRequests(context, payload = {}) {
      try {
        context.commit("SET_LOADING", true);
        context.commit("CLEAR_ERROR");

        // Handle both direct dateRange parameter and payload object
        let dateRange = null;
        let params = {};

        if (payload.dateRange) {
          // If payload contains dateRange property
          dateRange = payload.dateRange;
        } else if (
          typeof payload === "object" &&
          !payload.hasOwnProperty("status")
        ) {
          // If payload is the dateRange object itself
          dateRange = payload;
        }

        // Use provided dateRange or get from global store
        const range = dateRange || context.rootGetters.getGlobalDateRange;

        // Add date range parameters to the request
        params = {
          from: range.from,
          to: range.to,
        };

        // Add status parameter if provided
        if (payload.status) {
          params.status = payload.status;
        }

        const apiKey = context.getters.getterApikey;
        const url = `${pettyCashApi}requests/all`;

        const response = await axios.get(url, {
          params,
          headers: {
            Authorization: `Bearer ${apiKey}`,
          },
        });

        if (response.data.success) {
          context.commit("SET_REQUESTS", response.data.data);
          return response.data.data;
        } else {
          throw new Error(response.data.message || "Failed to fetch requests");
        }
      } catch (error) {
        context.commit(
          "SET_ERROR",
          error.message || "An error occurred while fetching requests"
        );
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit("SET_LOADING", false);
      }
    },

    // Alias for fetchUserRequests for backward compatibility
    async getUserRequests(context, payload) {
      return context.dispatch("fetchUserRequests", payload);
    },

    // Fetch user requests with optional date range filtering
    async fetchUserRequests(context, payload = {}) {
      try {
        context.commit("SET_LOADING", true);
        context.commit("CLEAR_ERROR");
        const url = `${pettyCashApi}api/expense-requests/user/${payload.user.id}?all=true`;

        const response = await axios.get(url);

        if (response.data.success) {
          context.commit("SET_REQUESTS", response.data.userRequests);
          return response.data.userRequests;
        } else {
          throw new Error(
            response.data.message || "Failed to fetch user requests"
          );
        }
      } catch (error) {
        context.commit(
          "SET_ERROR",
          error.message || "An error occurred while fetching user requests"
        );
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit("SET_LOADING", false);
      }
    },

    async createRequest(context, payload) {
      try {
        context.commit("SET_LOADING", true);
        context.commit("CLEAR_ERROR");

        const apiKey = context.getters.getterApikey;
        const url = `${pettyCashApi}requests/create`;

        const response = await axios.post(url, payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiKey}`,
          },
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || "Failed to create request");
        }
      } catch (error) {
        context.commit(
          "SET_ERROR",
          error.message || "Failed to create request"
        );
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit("SET_LOADING", false);
      }
    },

    async uploadImages(context, { requestId, images }) {
      try {
        context.commit("SET_LOADING", true);
        context.commit("CLEAR_ERROR");

        const apiKey = context.getters.getterApikey;
        const url = `${pettyCashApi}requests/${requestId}/images`;

        const formData = new FormData();
        for (let i = 0; i < images.length; i++) {
          formData.append("images", images[i]);
        }

        const response = await axios.post(url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${apiKey}`,
          },
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || "Failed to upload images");
        }
      } catch (error) {
        context.commit("SET_ERROR", error.message || "Failed to upload images");
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit("SET_LOADING", false);
      }
    },

    // Fetch a single request by ID
    async fetchRequestById(context, requestId) {
      try {
        context.commit("SET_LOADING", true);
        context.commit("CLEAR_ERROR");

        const url = `${pettyCashApi}api/expense-requests/${requestId}`;

        const response = await axios.get(url);

        if (response.data.success) {
          // Transform API response to match component expectations
          const expenseRequest = response.data.expenseRequest;
          const transformedRequest = {
            id: expenseRequest.id,
            title: expenseRequest.expenseType || "Expense Request",
            amount: expenseRequest.amount || 0,
            date: expenseRequest.requestDate || expenseRequest.createdAt,
            description: expenseRequest.reason || "No description provided",
            status: expenseRequest.status
              ? expenseRequest.status.toLowerCase()
              : "pending",
            category:
              expenseRequest.subType || expenseRequest.expenseType || "",
            images: expenseRequest.attachment?.url ? [expenseRequest.attachment.url] : [],
            transactionId: expenseRequest.transactionId || null,
            processedDate: expenseRequest.processedDate || null,
          };

          context.commit("SET_CURRENT_REQUEST", transformedRequest);
          return transformedRequest;
        } else {
          throw new Error(
            response.data.message || "Failed to fetch request details"
          );
        }
      } catch (error) {
        context.commit(
          "SET_ERROR",
          error.message || "An error occurred while fetching request details"
        );
        errorHandler.tomcatError(error);
        throw error;
      } finally {
        context.commit("SET_LOADING", false);
      }
    },
  },
};
